import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { InstallButton } from '@/context/InstallContext';

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [merchant, setMerchant] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [merchantRes, ordersRes] = await Promise.all([
        axios.get(`${API}/merchant/me`),
        axios.get(`${API}/merchant/orders`)
      ]);
      setMerchant(merchantRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      if (error.response?.status === 403) {
        logout();
        navigate('/merchant/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const markReady = async (orderId) => {
    try {
      await axios.put(`${API}/merchant/orders/${orderId}/ready`);
      toast.success('Order marked as ready for pickup');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const confirmHandover = async (orderId) => {
    try {
      await axios.put(`${API}/merchant/orders/${orderId}/confirm-handover`);
      toast.success('Handover confirmed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm handover');
    }
  };

  const confirmPayment = async (orderId) => {
    try {
      await axios.put(`${API}/merchant/orders/${orderId}/confirm-payment`);
      toast.success('Payment confirmed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'ready_for_pickup': return 'bg-blue-100 text-blue-800';
      case 'picked_up': return 'bg-orange-100 text-orange-800';
      case 'delivered': return 'bg-purple-100 text-purple-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'payment_confirmed': return 'bg-green-200 text-green-900';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'ready_for_pickup': return 'Ready for Pickup';
      case 'picked_up': return 'Picked Up';
      case 'delivered': return 'Delivered';
      case 'paid': return 'Payment Sent';
      case 'payment_confirmed': return 'Completed';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const readyOrders = orders.filter(o => o.status === 'ready_for_pickup');
  const pickedUpOrders = orders.filter(o => o.status === 'picked_up');
  const awaitingPaymentConfirm = orders.filter(o => o.status === 'paid');
  const completedOrders = orders.filter(o => ['delivered', 'payment_confirmed'].includes(o.status));

  return (
    <div className="min-h-screen bg-background" data-testid="merchant-dashboard">
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <span className="text-xl font-heading font-bold text-foreground block">EXCEL ERA</span>
                <span className="text-xs text-secondary font-bold uppercase tracking-wide">Merchant Portal</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <InstallButton variant="default" size="sm" />
              <span className="text-sm font-medium text-muted-foreground hidden md:inline">{merchant?.business_name}</span>
              <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="font-bold uppercase text-sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Banner */}
        <div className="bg-secondary text-white p-6 rounded-sm shadow-hard mb-8">
          <h1 className="text-2xl font-heading font-bold mb-1">Welcome, {merchant?.contact_person}</h1>
          <p className="text-sm opacity-90">{merchant?.business_name} • {merchant?.phone}</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-yellow-600 mb-2">{pendingOrders.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">New Orders</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-blue-600 mb-2">{readyOrders.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Ready for Pickup</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-orange-600 mb-2">{pickedUpOrders.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Awaiting Handover</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm">
            <div className="text-4xl font-heading font-black text-green-600 mb-2">{awaitingPaymentConfirm.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Confirm Payment</div>
          </div>
        </div>

        {/* Products Info */}
        <div className="bg-white border-2 border-border p-6 rounded-sm mb-8">
          <h2 className="text-lg font-heading font-bold text-foreground mb-4">Your Products</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-bold uppercase text-muted-foreground">Oil Brands:</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {merchant?.oil_brands?.length > 0 ? merchant.oil_brands.map((brand, i) => (
                  <span key={i} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-bold rounded-sm">{brand}</span>
                )) : <span className="text-muted-foreground">None listed</span>}
              </div>
            </div>
            <div>
              <span className="text-sm font-bold uppercase text-muted-foreground">Filter Brands:</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {merchant?.filter_brands?.length > 0 ? merchant.filter_brands.map((brand, i) => (
                  <span key={i} className="px-3 py-1 bg-green-100 text-green-800 text-sm font-bold rounded-sm">{brand}</span>
                )) : <span className="text-muted-foreground">None listed</span>}
              </div>
            </div>
          </div>
        </div>

        {/* New Orders - Need to Mark Ready */}
        {pendingOrders.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">New Orders - Prepare Items</h2>
            <div className="space-y-4">
              {pendingOrders.map((order) => (
                <div key={order.id} className="bg-white border-l-4 border-yellow-500 p-6 rounded-sm shadow-sm" data-testid="pending-order-card">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                        <span className="text-xs text-muted-foreground">Order #{order.id.slice(0, 8)}</span>
                      </div>
                      <div className="space-y-2">
                        <div className="font-bold text-foreground">Items:</div>
                        {order.items.map((item, i) => (
                          <div key={i} className="text-sm ml-4">
                            • {item.quantity}x {item.brand} ({item.type})
                          </div>
                        ))}
                        {order.total_amount && (
                          <p className="text-lg font-bold text-green-600 mt-2">Total: ₦{order.total_amount.toLocaleString()}</p>
                        )}
                        {order.notes && (
                          <p className="text-sm text-muted-foreground italic mt-2">&quot;{order.notes}&quot;</p>
                        )}
                        {order.technician_name && (
                          <p className="text-sm mt-2">
                            <span className="text-muted-foreground">Pickup by:</span>{' '}
                            <span className="font-bold">{order.technician_name}</span> ({order.technician_phone})
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Button
                        onClick={() => markReady(order.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
                        data-testid="mark-ready-btn"
                      >
                        Mark Ready for Pickup
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders Picked Up - Need to Confirm Handover */}
        {pickedUpOrders.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Confirm Handover</h2>
            <div className="space-y-4">
              {pickedUpOrders.map((order) => (
                <div key={order.id} className="bg-white border-l-4 border-orange-500 p-6 rounded-sm shadow-sm" data-testid="handover-order-card">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                        <span className="text-xs text-muted-foreground">Order #{order.id.slice(0, 8)}</span>
                      </div>
                      <p className="text-sm">
                        <span className="text-muted-foreground">Picked up by:</span>{' '}
                        <span className="font-bold">{order.technician_name}</span>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Picked at: {new Date(order.technician_picked_at).toLocaleString()}
                      </p>
                      {order.total_amount && (
                        <p className="text-lg font-bold text-green-600 mt-2">Total: ₦{order.total_amount.toLocaleString()}</p>
                      )}
                    </div>
                    <div className="flex items-start">
                      <Button
                        onClick={() => confirmHandover(order.id)}
                        className="bg-orange-600 hover:bg-orange-700 text-white font-bold"
                        data-testid="confirm-handover-btn"
                      >
                        Confirm Items Handed Over
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Sent - Need to Confirm Receipt */}
        {awaitingPaymentConfirm.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Confirm Payment Received</h2>
            <div className="space-y-4">
              {awaitingPaymentConfirm.map((order) => (
                <div key={order.id} className="bg-white border-l-4 border-green-500 p-6 rounded-sm shadow-sm" data-testid="payment-order-card">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                        <span className="text-xs text-muted-foreground">Order #{order.id.slice(0, 8)}</span>
                      </div>
                      <p className="text-lg font-bold text-green-600">Amount: ₦{order.total_amount?.toLocaleString() || 'N/A'}</p>
                      <p className="text-sm text-muted-foreground">
                        Payment sent: {new Date(order.company_paid_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <Button
                        onClick={() => confirmPayment(order.id)}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold"
                        data-testid="confirm-payment-btn"
                      >
                        ✓ Confirm Payment Received
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ready for Pickup */}
        {readyOrders.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Awaiting Technician Pickup</h2>
            <div className="space-y-4">
              {readyOrders.map((order) => (
                <div key={order.id} className="bg-white border-2 border-border p-6 rounded-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                    <span className="text-xs text-muted-foreground">Order #{order.id.slice(0, 8)}</span>
                  </div>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Technician:</span>{' '}
                    <span className="font-bold">{order.technician_name || 'Not assigned'}</span>
                    {order.technician_phone && ` (${order.technician_phone})`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ready since: {new Date(order.merchant_ready_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Orders */}
        <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Order History</h2>
        {completedOrders.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
            <p className="text-xl font-medium text-muted-foreground">No completed orders yet</p>
          </div>
        ) : (
          <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Order ID</th>
                    <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Items</th>
                    <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Amount</th>
                    <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {completedOrders.map(order => (
                    <tr key={order.id} className="hover:bg-accent transition-colors">
                      <td className="px-6 py-4 font-mono text-sm">{order.id.slice(0, 8)}</td>
                      <td className="px-6 py-4 text-sm">
                        {order.items.map((item, i) => (
                          <span key={i}>{item.quantity}x {item.brand}{i < order.items.length - 1 ? ', ' : ''}</span>
                        ))}
                      </td>
                      <td className="px-6 py-4 font-bold">₦{order.total_amount?.toLocaleString() || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
